import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, Check, X, Calendar, AlertCircle } from 'lucide-react';
import { useTaskSuggestions } from '@/hooks/useTaskSuggestions';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export function TaskSuggestionsDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { suggestions, isLoading, approveSuggestions, rejectSuggestions } = useTaskSuggestions();

  // Listen for custom event to open dialog
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-task-suggestions', handleOpen);
    return () => window.removeEventListener('open-task-suggestions', handleOpen);
  }, []);

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(new Set(suggestions.map(s => s.id)));
  };

  const handleApprove = () => {
    approveSuggestions.mutate(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleReject = () => {
    rejectSuggestions.mutate(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  if (suggestions.length === 0 && !isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Review Task Suggestions
          </DialogTitle>
          <DialogDescription>
            AI found {suggestions.length} potential action item{suggestions.length !== 1 ? 's' : ''} from your emails. 
            Select which ones to add to your task list.
          </DialogDescription>
        </DialogHeader>

        {/* Bulk actions */}
        <div className="flex items-center justify-between border-b pb-3">
          <Button variant="ghost" size="sm" onClick={selectAll}>
            Select All ({suggestions.length})
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              disabled={selectedIds.size === 0 || rejectSuggestions.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Dismiss ({selectedIds.size})
            </Button>
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={selectedIds.size === 0 || approveSuggestions.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              Create Tasks ({selectedIds.size})
            </Button>
          </div>
        </div>

        {/* Suggestions list */}
        <ScrollArea className="h-96">
          <div className="space-y-3">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className={cn(
                  "border rounded-lg p-4 cursor-pointer transition-colors",
                  selectedIds.has(suggestion.id) ? "bg-primary/10 border-primary ring-2 ring-primary/20" : "hover:bg-muted/50"
                )}
                onClick={() => toggleSelect(suggestion.id)}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedIds.has(suggestion.id)}
                    onCheckedChange={() => toggleSelect(suggestion.id)}
                    className="mt-1"
                  />
                  
                  <div className="flex-1 space-y-2">
                    {/* Title & Priority */}
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm">{suggestion.title}</h4>
                      <Badge variant={
                        suggestion.priority === 'critical' ? 'destructive' :
                        suggestion.priority === 'high' ? 'default' : 'secondary'
                      }>
                        {suggestion.priority}
                      </Badge>
                    </div>

                    {/* Description */}
                    {suggestion.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {suggestion.description}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {suggestion.due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due: {format(new Date(suggestion.due_date), 'MMM d')}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {Math.round(suggestion.confidence_score * 100)}% confidence
                      </div>
                    </div>

                    {/* Extraction context */}
                    {suggestion.extraction_context && (
                      <div className="bg-muted rounded p-2 text-xs italic">
                        "{suggestion.extraction_context}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
